import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './SellerHVIDetailsForm.css';

const SellerHVIDetailsForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    companyProfile: '',
    certificateOfIncorporation: null,
    gstFile: null,
    companyPanCard: null,
    msmeType: 'MSME',
    msmeRegistration: null,
    cancelCheque: null,

    // New Fields
    directors: [{ name: '', mobile: '', email: '' }],
    authorizedDirectorIndex: '', // Index of the director who is authorized

    officeAddress: { street: '', city: '', state: '', pincode: '', country: 'India' },
    unitAddresses: [{ street: '', city: '', state: '', pincode: '', country: 'India', unitType: 'unit' }],

    gstAddressChecked: false, // "Take from GST" checkbox state
    declarationAccepted: false,
    declarationOtp: ''
  });

  const [validationOtpSent, setValidationOtpSent] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState('');

  // Handle Basic Inputs
  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // Handle File Uploads
  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Please upload only PDF files'); return;
      }
      setFormData(prev => ({ ...prev, [fieldName]: file }));
      setError('');
    }
  };

  // --- Director Logic ---
  const handleDirectorChange = (index, field, value) => {
    const updatedDirectors = [...formData.directors];
    updatedDirectors[index][field] = value;
    setFormData({ ...formData, directors: updatedDirectors });
  };

  const addDirector = () => {
    setFormData(prev => ({
      ...prev,
      directors: [...prev.directors, { name: '', mobile: '', email: '' }]
    }));
  };

  const removeDirector = (index) => {
    if (formData.directors.length === 1) return;
    const updated = formData.directors.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      directors: updated,
      authorizedDirectorIndex: prev.authorizedDirectorIndex === index ? '' : (prev.authorizedDirectorIndex > index ? prev.authorizedDirectorIndex - 1 : prev.authorizedDirectorIndex)
    }));
  };

  // --- Address Logic ---
  const handleOfficeAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      officeAddress: { ...prev.officeAddress, [name]: value }
    }));
  };

  const handleUnitAddressChange = (index, field, value) => {
    const updatedUnits = [...formData.unitAddresses];
    updatedUnits[index][field] = value;
    setFormData({ ...formData, unitAddresses: updatedUnits });
  };

  const addUnitAddress = () => {
    setFormData(prev => ({
      ...prev,
      unitAddresses: [...prev.unitAddresses, { street: '', city: '', state: '', pincode: '', country: 'India', unitType: 'unit' }]
    }));
  };

  const removeUnitAddress = (index) => {
    if (formData.unitAddresses.length === 1) return;
    const updated = formData.unitAddresses.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, unitAddresses: updated }));
  };

  // --- Declaration & OTP ---
  const sendDeclarationOtp = () => {
    if (formData.directors.some(d => !d.mobile)) {
      setError('Please provide mobile numbers for all directors first.');
      return;
    }
    // Simulate sending OTP to Authorized person
    const mockOtp = '123456';
    setGeneratedOtp(mockOtp);
    alert(`OTP sent to Authorized Person: ${mockOtp}`);
    setValidationOtpSent(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.declarationAccepted) { setError('Please accept the declaration.'); return; }
    if (validationOtpSent && formData.declarationOtp !== generatedOtp) { setError('Invalid Declaration OTP'); return; }

    setLoading(true);
    // Mock Submit
    setTimeout(() => {
      setLoading(false);
      navigate('/role-selection');
    }, 1500);
  };

  return (
    <div className="business-form-container animate-fade-in">
      <div className="business-form-card">
        <div className="form-header">
          <h1 className="form-logo">
            <span className="dark-blue-letter">G</span><span className="light-blue-letter">o</span>
            <span className="dark-blue-letter">E</span><span className="light-blue-letter">asy</span>
            <span className="dark-blue-letter">T</span><span className="light-blue-letter">rade</span>
          </h1>
          <h2>Business Verification & Profile</h2>
          <p className="subtitle">Complete your KYC to start trading on the global marketplace</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="premium-form kyc-form-grid-layout">
          {/* Left Column: Identity, Docs, Directors */}
          <div className="kyc-form-column">
            {/* 1. Company Profile */}
            <div className="form-section-group">
              <h3>Company Identity</h3>
              <div className="form-group" style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Select Your Business Profile *</label>
                <select name="companyProfile" value={formData.companyProfile} onChange={handleInputChange} required style={{ padding: '8px 10px', fontSize: '0.85rem' }}>
                  <option value="">-- Select Profile --</option>
                  <option value="MILL">Textile Mill / Spinning Unit</option>
                  <option value="GIN">Ginning Factory</option>
                  <option value="MNC Trader">MNC Trading House</option>
                  <option value="Individual/MSME trade">Individual / MSME Trader</option>
                  <option value="government">Government Agency</option>
                  <option value="others">Others</option>
                </select>
              </div>
            </div>

            {/* 2. Upload Documents */}
            <div className="form-section-group">
              <h3>Compliance Documents (PDF)</h3>
              <p className="helper-text-premium" style={{ marginBottom: '10px', fontSize: '0.8rem' }}>Please upload clear PDF copies of the following:</p>

              {[
                { id: 'certificateOfIncorporation', label: 'Incorporation Deed / Partners' },
                { id: 'gstFile', label: 'GST Certificate' },
                { id: 'companyPanCard', label: 'Company PAN Card' },
                { id: 'cancelCheque', label: 'Cancelled Cheque' },
                { id: 'msmeRegistration', label: 'MSME / Udyam Certificate' }
              ].map((doc) => (
                <div className="file-upload-group" key={doc.id} style={{ marginBottom: '8px' }}>
                  <div className="file-upload-right-align" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', gap: '10px' }}>
                    <label className="doc-label" style={{ fontSize: '0.8rem', margin: 0 }}>{doc.label} *</label>
                    <div className="file-upload">
                      <input type="file" accept=".pdf" onChange={(e) => handleFileChange(e, doc.id)} required={!formData[doc.id]} />
                      <span className="file-name" style={{ padding: '6px 10px', fontSize: '0.75rem' }}>{formData[doc.id] ? formData[doc.id].name : '📤 Upload PDF'}</span>
                    </div>
                  </div>
                </div>
              ))}

              <div className="form-group" style={{ marginTop: '12px', marginBottom: 0 }}>
                <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>MSME Classification *</label>
                <select name="msmeType" value={formData.msmeType} onChange={handleInputChange} style={{ padding: '8px 10px', fontSize: '0.85rem' }}>
                  <option value="MSME">Registered MSME</option>
                  <option value="Non-MSME">Non-MSME (Self-Declaration Required)</option>
                </select>
              </div>
            </div>

            {/* 3. Contact Details (Directors) */}
            <div className="form-section-group">
              <h3>Management Details</h3>
              <p className="helper-text-premium" style={{ marginBottom: '8px', fontSize: '0.8rem' }}>Provide details of active Directors or Partners.</p>
              {formData.directors.map((director, index) => (
                <div key={index} className="director-block" style={{ padding: '10px', marginBottom: '8px', borderRadius: '8px' }}>
                  <div className="block-header" style={{ marginBottom: '6px' }}>
                    <h4 style={{ fontSize: '0.8rem' }}>Director / Partner {index + 1}</h4>
                    {formData.directors.length > 1 && (
                      <button type="button" className="remove-btn" onClick={() => removeDirector(index)} style={{ padding: '3px 8px', fontSize: '0.7rem' }}>Delete</button>
                    )}
                  </div>
                  <div className="form-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '6px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: '0.75rem', marginBottom: '2px' }}>Full Name</label>
                      <input type="text" value={director.name} onChange={(e) => handleDirectorChange(index, 'name', e.target.value)} required placeholder="As per PAN" style={{ padding: '6px 10px', fontSize: '0.8rem' }} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: '0.75rem', marginBottom: '2px' }}>Mobile Number</label>
                      <input type="tel" value={director.mobile} onChange={(e) => handleDirectorChange(index, 'mobile', e.target.value)} required placeholder="+91 00000 00000" style={{ padding: '6px 10px', fontSize: '0.8rem' }} />
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem', marginBottom: '2px' }}>Official Email ID</label>
                    <input type="email" value={director.email} onChange={(e) => handleDirectorChange(index, 'email', e.target.value)} required placeholder="email@company.com" style={{ padding: '6px 10px', fontSize: '0.8rem' }} />
                  </div>
                </div>
              ))}
              <button type="button" className="add-btn" onClick={addDirector} style={{ padding: '8px', fontSize: '0.8rem' }}>+ Add Another Management Member</button>
            </div>

            {/* 4. Authorized Person */}
            <div className="form-section-group auth-highlight">
              <h3>Official Authorization</h3>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Designate Authorized Representative *</label>
                <select
                  value={formData.authorizedDirectorIndex}
                  onChange={(e) => setFormData({ ...formData, authorizedDirectorIndex: e.target.value })}
                  required
                  style={{ padding: '8px 10px', fontSize: '0.85rem' }}
                >
                  <option value="">-- Select from above --</option>
                  {formData.directors.map((d, i) => (
                    <option key={i} value={i}>{d.name || `Director ${i + 1}`}</option>
                  ))}
                </select>
              </div>
              {formData.authorizedDirectorIndex !== '' && (
                <div className="auth-preview-premium" style={{ padding: '12px', marginTop: '10px', borderRadius: '8px' }}>
                  <div className="auth-badge" style={{ fontSize: '0.65rem', padding: '3px 8px', marginBottom: '6px' }}>AUTHORIZED SIGNATORY</div>
                  <p style={{ margin: '2px 0', fontSize: '0.8rem' }}><strong>Name:</strong> {formData.directors[formData.authorizedDirectorIndex].name}</p>
                  <p style={{ margin: '2px 0', fontSize: '0.8rem' }}><strong>Role:</strong> Responsible for all bidding and trade approvals.</p>
                  <div className="checkbox-group" style={{ margin: '8px 0 0 0' }}>
                    <input type="checkbox" id="auth-check" required />
                    <label htmlFor="auth-check" style={{ fontSize: '0.78rem' }}>I certify that this person is authorized with the legal consent of all directors/partners.</label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Addresses, Declaration, Submit */}
          <div className="kyc-form-column">
            {/* 5. Address Details */}
            <div className="form-section-group">
              <h3>Registered Address (As per GST)</h3>

              <div className="address-block office-primary" style={{ padding: '10px', borderRadius: '8px', marginBottom: '10px' }}>
                <h4 style={{ fontSize: '0.8rem', marginBottom: '6px' }}>Head Office / Registered Office</h4>
                <input type="text" name="street" value={formData.officeAddress.street} onChange={handleOfficeAddressChange} placeholder="Plot No, Street, Area" required style={{ padding: '6px 10px', fontSize: '0.8rem', marginBottom: '6px' }} />
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '6px' }}>
                  <input type="text" name="city" value={formData.officeAddress.city} onChange={handleOfficeAddressChange} placeholder="City / Town" required style={{ padding: '6px 10px', fontSize: '0.8rem', marginBottom: 0 }} />
                  <input type="text" name="state" value={formData.officeAddress.state} onChange={handleOfficeAddressChange} placeholder="State" required style={{ padding: '6px 10px', fontSize: '0.8rem', marginBottom: 0 }} />
                </div>
                <input type="text" name="pincode" value={formData.officeAddress.pincode} onChange={handleOfficeAddressChange} placeholder="Pincode (6 Digits)" required style={{ padding: '6px 10px', fontSize: '0.8rem', marginBottom: 0 }} />
              </div>

              <h4 style={{ marginTop: '15px', marginBottom: '8px', color: '#0d47a1', fontSize: '0.85rem' }}>Production / Godown Units</h4>
              <div className="checkbox-group auto-fill-box" style={{ padding: '8px 12px', margin: '0 0 10px 0' }}>
                <input
                  type="checkbox"
                  id="gst-auto"
                  checked={formData.gstAddressChecked}
                  onChange={(e) => setFormData({ ...formData, gstAddressChecked: e.target.checked })}
                />
                <label htmlFor="gst-auto" style={{ fontSize: '0.8rem' }}>Auto-fill unit addresses from GST certificate</label>
              </div>

              {formData.unitAddresses.map((unit, index) => (
                <div key={index} className="unit-block address-block" style={{ padding: '10px', borderRadius: '8px', marginBottom: '8px' }}>
                  <div className="block-header" style={{ marginBottom: '6px' }}>
                    <h5 style={{ fontSize: '0.8rem' }}>Operational Unit {index + 1}</h5>
                    {formData.unitAddresses.length > 1 && (
                      <button type="button" className="remove-btn" onClick={() => removeUnitAddress(index)} style={{ padding: '3px 8px', fontSize: '0.7rem' }}>Remove</button>
                    )}
                  </div>
                  <input type="text" value={unit.street} onChange={(e) => handleUnitAddressChange(index, 'street', e.target.value)} placeholder="Unit Full Street Address" required style={{ padding: '6px 10px', fontSize: '0.8rem', marginBottom: '6px' }} />
                  <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '6px' }}>
                    <input type="text" value={unit.city} onChange={(e) => handleUnitAddressChange(index, 'city', e.target.value)} placeholder="City" required style={{ padding: '6px 10px', fontSize: '0.8rem', marginBottom: 0 }} />
                    <input type="text" value={unit.state} onChange={(e) => handleUnitAddressChange(index, 'state', e.target.value)} placeholder="State" required style={{ padding: '6px 10px', fontSize: '0.8rem', marginBottom: 0 }} />
                  </div>
                  <input type="text" value={unit.pincode} onChange={(e) => handleUnitAddressChange(index, 'pincode', e.target.value)} placeholder="Pincode" required style={{ padding: '6px 10px', fontSize: '0.8rem', marginBottom: 0 }} />
                </div>
              ))}
              <button type="button" className="add-btn" onClick={addUnitAddress} style={{ padding: '8px', fontSize: '0.8rem' }}>+ Add Additional Operating Unit</button>
            </div>

            {/* 6. Declaration */}
            <div className="form-section-group declaration-section-premium">
              <h3>Legal Declaration</h3>
              <div className="declaration-card" style={{ padding: '12px', marginBottom: '10px' }}>
                <p style={{ fontSize: '0.8rem', margin: 0, lineHeight: '1.4' }}>
                  I/We Proprietor/ Partner/ Director hereby declare that the information given above is true and correct to the best of my knowledge. I/We shall abide by all the terms & conditions for purchase of ready cotton bales from GoEasyTrade or its authorised sellers.
                </p>
              </div>

              <div className="otp-flow-container" style={{ marginTop: '10px' }}>
                {!validationOtpSent ? (
                  <button type="button" className="btn-secondary-premium" onClick={sendDeclarationOtp} style={{ padding: '10px 20px', fontSize: '0.85rem' }}>Verify Identity via Mobile OTP</button>
                ) : (
                  <div className="otp-verification-tray">
                    <label style={{ fontSize: '0.8rem' }}>Enter 6-Digit Verification Code</label>
                    <input type="text" placeholder="● ● ● ● ● ●" value={formData.declarationOtp} onChange={(e) => setFormData({ ...formData, declarationOtp: e.target.value })} maxLength="6" className="otp-input-premium" style={{ padding: '8px 12px', fontSize: '1rem' }} />
                    <div className="checkbox-group" style={{ margin: '8px 0 0 0' }}>
                      <input type="checkbox" id="e-sign" checked={formData.declarationAccepted} onChange={(e) => setFormData({ ...formData, declarationAccepted: e.target.checked })} required />
                      <label htmlFor="e-sign" style={{ fontSize: '0.78rem' }}>I legally accept this declaration as a digital signature.</label>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-submit-tray" style={{ marginTop: '20px' }}>
              <button type="submit" className="btn-primary-large" disabled={loading} style={{ padding: '12px 30px', fontSize: '1rem' }}>
                {loading ? 'Processing Registration...' : 'COMPLETE BUSINESS ONBOARDING'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SellerHVIDetailsForm;
